import React, { useState, useEffect } from 'react';
import { Save, Wand2 } from 'lucide-react';

/**
 * LearningPanel - Raw OCR Result Editor for PaddleOCR Fine-tuning
 * 
 * @param {Array} ocrResults - Raw OCR results from smart-label response
 * @param {String} imagePath - Path to the current image
 * @param {String} apiBaseUrl - Backend API URL
 * @param {Function} onHighlight - Callback to highlight box on canvas when row is hovered/selected
 */
const LearningPanel = ({ ocrResults = [], imagePath, imageUrl, fileName, apiBaseUrl, geminiFields = [], onHighlight, externalSelectedId }) => {
    const [rows, setRows] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    // Sync with external selection
    useEffect(() => {
        if (externalSelectedId) {
            setSelectedId(externalSelectedId);
        }
    }, [externalSelectedId]);

    // Initial load - Always use current ocrResults from Fields
    useEffect(() => {
        // ⭐ Always use current ocrResults (PaddleOCR extracted data from Fields)
        // Do NOT load saved data - user wants fresh extracted data each time
        if (ocrResults && ocrResults.length > 0) {
            console.log('📋 Loading current OCR results:', ocrResults.length, 'items');
            setRows(ocrResults.map(item => {
                let points = item.box;
                const isInvalidBox = !points || points.length === 0 || (
                    Array.isArray(points) && points.every(p => p[0] === 0 && p[1] === 0)
                );

                if (isInvalidBox && item.bbox) {
                    const { x, y, width, height } = item.bbox;
                    points = [[x, y], [x + width, y], [x + width, y + height], [x, y + height]];
                }
                return {
                    id: item.id,
                    text: item.text,
                    points: points || [],
                    isModified: false
                };
            }));
        }
    }, [ocrResults]);

    // Handle Text Change
    const handleTextChange = (id, newText) => {
        setRows(prev => prev.map(row =>
            row.id === id ? { ...row, text: newText, isModified: true } : row
        ));
    };

    // Handle Input KeyDown (Enter to next row)
    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            // Focus next input
            const nextInput = document.getElementById(`learning-input-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    // AI Auto-Correct
    const handleAutoCorrect = async () => {
        if (rows.length === 0) return;

        setIsSaving(true); // Re-use saving state for loading indicator
        try {
            const texts = rows.map(r => r.text);
            const response = await fetch(`${apiBaseUrl}/api/learning/auto-correct`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    texts,
                    reference: geminiFields
                })
            });

            const data = await response.json();
            if (data.success && data.corrected) {
                // Update rows with corrected text
                setRows(prev => prev.map((row, index) => {
                    const newText = data.corrected[index];
                    return {
                        ...row,
                        text: newText || row.text, // Fallback to original
                        isModified: newText !== row.text // Mark as modified if changed
                    };
                }));
                alert(`AI 자동 보정이 완료되었습니다.\n변경된 항목을 확인해주세요.`);
            } else {
                throw new Error(data.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Auto-Correct Error:', error);
            alert('AI 자동 보정 실패: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    // Save Training Data
    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Convert to PPOCRLabel format: 
            // List of objects: { transcription: "text", points: [[x,y], ...] }
            const trainingData = rows.map(row => ({
                transcription: row.text,
                points: row.points
            }));

            const formData = new FormData();
            formData.append('labels', JSON.stringify(trainingData));

            // Case 1: Image Path exists (Verified)
            if (imagePath) {
                formData.append('imagePath', imagePath);
            }
            // Case 2: Image URL exists (Direct Save)
            else if (imageUrl) {
                // Fetch blob from imageUrl
                const imgRes = await fetch(imageUrl);
                const blob = await imgRes.blob();
                // 🏷️ 파일명 지정 (Overwrite를 위해 원본명 사용)
                formData.append('image', blob, fileName || 'training_image.png');
                if (fileName) formData.append('fileName', fileName); // 명시적 필드 추가
            } else {
                alert('이미지 경로 또는 데이터를 찾을 수 없습니다.');
                setIsSaving(false);
                return;
            }

            const response = await fetch(`${apiBaseUrl}/api/learning/save-training-data`, {
                method: 'POST',
                // headers: { 'Content-Type': 'multipart/form-data' }, // browser sets boundary auto
                body: formData
            });

            const data = await response.json();
            if (data.success) {
                alert(`학습 데이터가 저장되었습니다.\n경로: ${data.savedPath}`);
                setRows(prev => prev.map(r => ({ ...r, isModified: false })));
            } else {
                throw new Error(data.error);
            }
        } catch (error) {
            console.error('Save Error:', error);
            alert('저장 실패: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    // Scroll to selected coordinate row
    const rowRefs = React.useRef({});
    useEffect(() => {
        if (selectedId && rowRefs.current[selectedId]) {
            rowRefs.current[selectedId].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [selectedId]);

    return (
        <div className="flex flex-col flex-1 min-h-0 bg-white">
            {/* Toolbar */}
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between shrink-0">
                <div className="flex justify-between items-center w-full">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        Recognition result <span className="text-xs font-normal text-gray-500">({rows.length})</span>
                    </h3>
                    <div className="flex gap-2">
                        <button
                            onClick={handleAutoCorrect}
                            disabled={isSaving || rows.length === 0}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium text-white transition-colors
                            ${isSaving || rows.length === 0 ? 'bg-purple-300 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 shadow-sm'}
                        `}
                        >
                            <Wand2 className="w-3.5 h-3.5" />
                            AI Auto-Correct
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium text-white transition-colors
                            ${isSaving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-sm'}
                        `}
                        >
                            <Save className="w-3.5 h-3.5" />
                            {isSaving ? 'Saving...' : 'Save Training Data'}
                        </button>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto min-h-0">
                <table className="w-full text-xs border-collapse table-fixed">
                    <thead className="sticky top-0 bg-gray-100/90 backdrop-blur z-10">
                        <tr>
                            <th className="px-2 py-1 text-center text-gray-500 font-medium border-b border-r w-[40px]">No.</th>
                            <th className="px-3 py-1 text-left text-gray-500 font-medium border-b">Transcription</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr
                                key={row.id}
                                className={`border-b group ${selectedId === row.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                onClick={() => {
                                    setSelectedId(row.id);
                                    if (onHighlight) onHighlight(row.id);
                                }}
                            >
                                <td className="px-2 py-0.5 text-center text-gray-400 border-r select-none w-[40px]">
                                    {index}
                                </td>
                                <td className="px-2 py-0.5">
                                    <input
                                        id={`learning-input-${index}`}
                                        type="text"
                                        value={row.text}
                                        onChange={(e) => handleTextChange(row.id, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        className={`w-full px-2 py-0.5 rounded border outline-none font-mono text-sm transition-colors ${row.isModified
                                            ? 'border-blue-300 bg-blue-50/50 text-blue-800'
                                            : 'border-transparent bg-transparent focus:bg-white focus:border-blue-300'
                                            }`}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Bottom: Detection Box Position */}
            <div className="h-[200px] border-t border-[#DADCE0] bg-white flex flex-col shrink-0">
                <div className="px-3 py-2 bg-[#F8F9FA] border-b border-[#DADCE0] text-xs font-bold text-[#5F6368] flex items-center justify-between">
                    <span>Detection Box Position ({rows.length})</span>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {rows.map((row, idx) => {
                        const isSelected = selectedId === row.id;
                        // points format: [[x1,y1], [x2,y2], [x3,y3], [x4,y4]]
                        const [p1, p2, p3, p4] = row.points || [[0, 0], [0, 0], [0, 0], [0, 0]];
                        // Defensive coding if points are missing or malformed
                        const safePoints = [p1, p2, p3, p4].map(p => p ? p : [0, 0]);

                        const pointsStr = `[(${Math.round(safePoints[0][0])}, ${Math.round(safePoints[0][1])}), (${Math.round(safePoints[1][0])}, ${Math.round(safePoints[1][1])}), (${Math.round(safePoints[2][0])}, ${Math.round(safePoints[2][1])}), (${Math.round(safePoints[3][0])}, ${Math.round(safePoints[3][1])})]`;

                        return (
                            <div
                                key={row.id}
                                ref={el => rowRefs.current[row.id] = el}
                                onClick={() => {
                                    setSelectedId(row.id);
                                    if (onHighlight) onHighlight(row.id);
                                }}
                                className={`px-3 py-1 border-b border-[#F1F3F4] text-xs font-mono cursor-pointer transition-colors flex gap-2
                                    ${isSelected ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50 text-gray-600'}
                                `}
                            >
                                <span className="text-gray-400 w-6 shrink-0">{idx}</span>
                                <span className="truncate">{pointsStr}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default LearningPanel;
